import { API_BASE_URL, API_ORIGIN } from '../config/api';
import { isRunwayGen4TurboEnabled } from './featureFlagsService';
import type { EnvironmentSnapshot } from '../types/EnvironmentTypes';
import type { GaAnalyticsPayload } from './ga4Client';
import {
  InsufficientPixlesError,
  NoActiveEntitlementError,
  throwForCreditGate,
} from './entitlementErrors';

// Re-exported so existing callers keep importing the typed errors from here.
export {
  InsufficientPixlesError,
  NoActiveEntitlementError,
} from './entitlementErrors';

interface GenerateCharacterVideoRequest {
  /**
   * Scene array from the script planner (prompts + per-scene durations travel
   * together). The backend shoots Scene 1 today; later scenes ride along for
   * multi-stitch with no contract change.
   */
  scenes: VideoScriptScene[];
  image: string;
  /**
   * Sole voice channel: character voice bindings (array for multi-character
   * support, one voice each). Reuses the script-stage reference shape; the
   * backend reads `name` + `voiceUrl` and constructs the model audio
   * references itself. Entry order defines the audio-reference binding.
   */
  characters?: ScriptCharacterReference[];
  model: 'gen4_turbo' | 'seedance2' | 'seedance2_5';
  /**
   * Native AI-generated soundtrack on the output. Always sent explicitly
   * (true or false) — never omitted. Independent of character `voiceUrl`
   * clips, which travel as input audio references either way.
   */
  generateAudio: boolean;
  /**
   * Reusable location for the scene, by value (mirrors characters[]):
   * name/description plus already-resolved reference image URLs. Omitted
   * when no environment is selected.
   */
  environment?: EnvironmentSnapshot;
  /**
   * Output resolution tier. `480p` requests a cheap draft preview that can
   * later be enhanced to 1080p. Omit to keep the backend default (720p).
   */
  resolution?: VideoResolution;
  /**
   * Enhances a previously generated 480p draft to 1080p. When set, the
   * backend reuses the preview's inputs, so `scenes`/`image`/`characters`/
   * `environment`/`resolution` must NOT be sent (400 otherwise). This is the
   * raw wire field; prefer `handleVideoDraftEnhancement`.
   */
  draftTaskId?: string;
  /**
   * Idempotency key for resumable generations (8-128 chars `[A-Za-z0-9_-]`).
   * Minted fresh per click; never reused after a terminal failure.
   */
  clientRequestId?: string;
  /** Optional GA4 attribution context. */
  analytics?: GaAnalyticsPayload;
}

/** Draft-enhancement request: only the preview handle + idempotency key. */
interface EnhanceVideoRequest {
  draftTaskId: string;
  clientRequestId: string;
}

/** Live-action -> 2D animation request. `sourceVideo` is a relative blob path. */
interface GenerateLiveActionAnimationRequest {
  videoId: string;
  sourceVideo: string;
  prompt: string;
  model: 'kling_4';
  /** `generate` uses Kling's soundtrack; `preserve` keeps source audio; `none` is silent. */
  audioMode: 'generate' | 'preserve' | 'none';
  /** Optional single reference character used to recast the main subject. */
  characters?: ScriptCharacterReference[];
  resolution?: VideoResolution;
  clientRequestId: string;
  /** Optional GA4 attribution context. */
  analytics?: GaAnalyticsPayload;
}

/** Wire payloads accepted by POST /generate/video. */
type VideoRequestPayload =
  | GenerateCharacterVideoRequest
  | EnhanceVideoRequest
  | GenerateLiveActionAnimationRequest;

export interface GenerateCharacterVideoResponse {
  videoUrl: string;
  duration?: number;
  data?: any;
  pixlesCharged?: number;
  available?: number | null;
  /** Output resolution of this result (480p draft | 720p | 1080p). */
  resolution?: VideoResolution;
  /** Resolved clip length in seconds (after per-model snapping). */
  durationSeconds?: number;
  /** Set on a 480p draft result: the handle to enhance it to 1080p. */
  draftTaskId?: string;
  /** True when this result is an enhanceable 480p draft preview. */
  canEnhance?: boolean;
  /** For an enhancement result: the draft generationId it came from. */
  enhancedFrom?: string;
}

/**
 * Complete character reference forwarded to the script planner alongside the
 * user prompt. `image` is the already-resolved preview (signed HTTPS URL for
 * library characters, dataURL for fresh uploads) so the backend can see the
 * character. `voiceUrl` is the resolved signed voice-reference URL, if any.
 */
export interface ScriptCharacterReference {
  id?: string;
  name?: string;
  description?: string;
  species?: string;
  gender?: string;
  personality?: string[];
  biography?: string;
  tags?: string[];
  imageName?: string;
  image?: string;
  voiceUrl?: string;
}

export type VideoGenerationModel =
  | 'gen4_turbo'
  | 'seedance2'
  | 'seedance2_5'
  | 'gen4alpha';

/** Output resolution tiers supported by the video pipeline. */
export type VideoResolution = '480p' | '720p' | '1080p';

/**
 * Fallback Pixels/second by resolution, matching the backend
 * `VIDEO_RESOLUTION_RATES`. The `/generate/script` response publishes the
 * authoritative map (`resolutionPixelsPerSecond`); this is only used before
 * a script exists.
 */
export const VIDEO_PIXELS_PER_SECOND: Record<VideoResolution, number> = {
  '480p': 20,
  '720p': 30,
  '1080p': 68,
};

export interface GenerateScriptOptions {
  /** Character image preview to send when no library character is selected. */
  image?: string;
  /** Character references for library selections (array for multi-character support). */
  characters?: ScriptCharacterReference[];
  /**
   * Target video model: the backend caps per-scene durations to what this
   * model can generate in one shot (gen4 family = 5s, seedance2 = 15s,
   * seedance2_5 = 30s). Send the same model the video stage will use.
   */
  model?: VideoGenerationModel;
  /**
   * Reusable location for the scene, by value (mirrors characters[]):
   * name/description plus already-resolved reference image URLs. Omitted
   * when no environment is selected.
   */
  environment?: EnvironmentSnapshot;
  /** Optional GA4 attribution context; eventId identifies this planner run. */
  analytics?: GaAnalyticsPayload;
}

interface GenerateScriptRequest {
  userPrompt: string;
  image?: string;
  characters?: ScriptCharacterReference[];
  model?: VideoGenerationModel;
  environment?: EnvironmentSnapshot;
  analytics?: GaAnalyticsPayload;
}

export interface VideoScriptScene {
  scene: number;
  prompt: string;
  estimatedDurationSeconds: number;
}

interface GenerateScriptResponse {
  /** Scene-by-scene prompts with per-scene duration estimates. */
  scenes: VideoScriptScene[];
  /** Total estimated playback seconds across scenes. */
  estimatedDurationSeconds: number;
  /**
   * Clip-duration bounds (integer seconds) the resolved model accepts —
   * drives the length input instead of a hardcoded range. Absent on older
   * backends; callers fall back to 4–30.
   */
  clipDuration?: { min: number; max: number };
  /**
   * Output resolutions the resolved model accepts. Server-published so the
   * picker doesn't hardcode the list; `480p` (draft preview) is omitted when
   * the active provider can't produce it. Absent on older backends.
   */
  resolutions?: VideoResolution[];
  /** Tier selected by default (one of `resolutions`). */
  defaultResolution?: VideoResolution;
  /** Authoritative Pixels/second by resolution. */
  resolutionPixelsPerSecond?: Record<string, number>;
}

const getVideoUrlFromResponse = (responseData: any): string | undefined => {
  if (typeof responseData?.videoUrl === 'string') return responseData.videoUrl;
  if (typeof responseData?.url === 'string') return responseData.url;
  if (typeof responseData?.data?.videoUrl === 'string')
    return responseData.data.videoUrl;
  if (typeof responseData?.data?.url === 'string') return responseData.data.url;
  if (Array.isArray(responseData?.data)) {
    const item = responseData.data.find(
      (entry: any) => entry?.videoUrl || entry?.url
    );
    return item?.videoUrl || item?.url;
  }
  return undefined;
};

export const generateVideoScript = async (
  userPrompt: string,
  token: string,
  options?: GenerateScriptOptions
): Promise<GenerateScriptResponse> => {
  const data: GenerateScriptRequest = {
    userPrompt,
    ...(options?.image ? { image: options.image } : {}),
    ...(options?.characters?.length ? { characters: options.characters } : {}),
    ...(options?.model ? { model: options.model } : {}),
    ...(options?.environment ? { environment: options.environment } : {}),
    ...(options?.analytics ? { analytics: options.analytics } : {}),
  };

  const response = await fetch(`${API_BASE_URL}/generate/script`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const responseData = await response.json();

  if (!response.ok) {
    // 402/403 throw the shared typed errors (same upsell handling as video).
    throwForCreditGate(response.status, responseData, {
      insufficient: 'Insufficient pixels for this script',
      noEntitlement: 'An active subscription is required for this script',
      failed: 'Failed to generate script',
    });
  }

  // Strict scenes contract: no legacy flat-text fallbacks.
  const scenes = responseData?.scenes;
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error('Script generation response did not include any scenes');
  }
  for (const entry of scenes) {
    if (
      typeof entry?.prompt !== 'string' ||
      !entry.prompt.trim() ||
      typeof entry?.estimatedDurationSeconds !== 'number'
    ) {
      throw new Error('Script generation response contained an invalid scene');
    }
  }

  const clipDuration = responseData?.clipDuration;
  const hasBounds =
    typeof clipDuration?.min === 'number' &&
    typeof clipDuration?.max === 'number' &&
    clipDuration.min <= clipDuration.max;

  const rawResolutions = responseData?.resolutions;
  const resolutions: VideoResolution[] | undefined = Array.isArray(
    rawResolutions
  )
    ? rawResolutions.filter(
        (tier: unknown): tier is VideoResolution =>
          tier === '480p' || tier === '720p' || tier === '1080p'
      )
    : undefined;
  const defaultResolution =
    resolutions && resolutions.includes(responseData?.defaultResolution)
      ? (responseData.defaultResolution as VideoResolution)
      : resolutions?.[0];
  const rawRates = responseData?.resolutionPixelsPerSecond;
  const resolutionPixelsPerSecond =
    rawRates && typeof rawRates === 'object'
      ? (rawRates as Record<string, number>)
      : undefined;

  return {
    scenes: scenes.map((entry: any, index: number) => ({
      scene: typeof entry.scene === 'number' ? entry.scene : index + 1,
      prompt: entry.prompt.trim(),
      estimatedDurationSeconds: entry.estimatedDurationSeconds,
    })),
    estimatedDurationSeconds:
      typeof responseData?.estimatedDurationSeconds === 'number'
        ? responseData.estimatedDurationSeconds
        : scenes.reduce(
            (total: number, entry: any) =>
              total + (entry.estimatedDurationSeconds || 0),
            0
          ),
    ...(hasBounds
      ? { clipDuration: { min: clipDuration.min, max: clipDuration.max } }
      : {}),
    ...(resolutions?.length ? { resolutions } : {}),
    ...(defaultResolution ? { defaultResolution } : {}),
    ...(resolutionPixelsPerSecond ? { resolutionPixelsPerSecond } : {}),
  };
};

export interface VideoProgressEvent {
  status: string;
  message?: string;
  /** Completion percentage (0-100), sent ~every 10s. */
  progress?: number;
  /**
   * 'provider' = the provider's own progress value (Runway reports one);
   * 'estimated' = time-based fallback (Seedance only returns a status).
   */
  progressSource?: 'provider' | 'estimated';
  elapsedSeconds?: number;
  /** Backend generation id, when the stream carries one. */
  generationId?: string;
  /** Pixles charged/expected for this generation, when reported. */
  pixlesCharged?: number;
  /** Output resolution of the run (480p draft | 720p | 1080p). */
  resolution?: VideoResolution;
  /** Resolved clip length in seconds (after per-model snapping). */
  durationSeconds?: number;
  /** Completed 480p draft: handle to enhance it to 1080p. */
  draftTaskId?: string;
  /** True when the completed result is an enhanceable 480p draft. */
  canEnhance?: boolean;
  /** For an enhancement run: the draft generationId it came from. */
  enhancedFrom?: string;
}

export const handleCharacterVideoGeneration = async (
  scenes: VideoScriptScene[],
  image: string,
  token: string,
  characters?: ScriptCharacterReference[],
  onProgress?: (event: VideoProgressEvent) => void,
  options?: {
    generateAudio?: boolean;
    environment?: EnvironmentSnapshot;
    resolution?: VideoResolution;
    analytics?: GaAnalyticsPayload;
  }
): Promise<GenerateCharacterVideoResponse> => {
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error('At least one scene is required to generate video');
  }
  for (const entry of scenes) {
    if (
      typeof entry?.prompt !== 'string' ||
      !entry.prompt.trim() ||
      typeof entry?.estimatedDurationSeconds !== 'number' ||
      !Number.isFinite(entry.estimatedDurationSeconds)
    ) {
      throw new Error(
        'Each scene needs a prompt and an estimatedDurationSeconds'
      );
    }
  }
  const data: GenerateCharacterVideoRequest = {
    scenes: scenes.map((entry, index) => ({
      scene: typeof entry.scene === 'number' ? entry.scene : index + 1,
      prompt: entry.prompt.trim(),
      estimatedDurationSeconds: entry.estimatedDurationSeconds,
    })),
    image,
    // Voices travel inside `characters[].voiceUrl`; the backend constructs
    // the model audio references itself (mapping fallback when the script
    // text doesn't carry a Voice mapping block, e.g. edited out).
    ...(characters?.length ? { characters } : {}),
    // Default video model is seedance2_5 (gen4_turbo only for the
    // feature-flag allowlist); the backend also accepts seedance2.
    model: isRunwayGen4TurboEnabled() ? 'gen4_turbo' : 'seedance2_5',
    // Native soundtrack flag, always explicit (default ON).
    generateAudio: options?.generateAudio ?? true,
    // Reusable location snapshot (by value, like characters[]).
    ...(options?.environment ? { environment: options.environment } : {}),
    // Output resolution tier (480p = enhanceable draft preview); omitted to
    // keep the backend default (720p).
    ...(options?.resolution ? { resolution: options.resolution } : {}),
    // Fresh idempotency key per click; never reused after a terminal failure.
    clientRequestId: mintClientRequestId(),
    ...(options?.analytics ? { analytics: options.analytics } : {}),
  };

  return await runResumableVideoGeneration(data, token, onProgress);
};

/**
 * Convert a live-action clip into 2D animation. The backend wraps `prompt`
 * with the conversion instruction, reads the source clip from our storage,
 * and runs Runway video-to-video in reference mode.
 */
export const handleLiveActionVideoAnimation = async (
  params: {
    videoId: string;
    sourceVideo: string;
    prompt: string;
    resolution?: VideoResolution;
    audioMode?: 'generate' | 'preserve' | 'none';
    characters?: ScriptCharacterReference[];
    analytics?: GaAnalyticsPayload;
  },
  token: string,
  onProgress?: (event: VideoProgressEvent) => void
): Promise<GenerateCharacterVideoResponse> => {
  if (!params.sourceVideo) {
    throw new Error('Select or upload a live-action video first.');
  }
  if (!params.prompt.trim()) {
    throw new Error('Describe the 2D animation style you want.');
  }
  const data: GenerateLiveActionAnimationRequest = {
    videoId: params.videoId,
    sourceVideo: params.sourceVideo,
    prompt: params.prompt.trim(),
    model: 'kling_4',
    audioMode: params.audioMode ?? 'generate',
    ...(params.characters?.length ? { characters: params.characters } : {}),
    ...(params.resolution ? { resolution: params.resolution } : {}),
    clientRequestId: mintClientRequestId(),
    ...(params.analytics ? { analytics: params.analytics } : {}),
  };
  return await runResumableVideoGeneration(
    data,
    token,
    onProgress,
    '/generate/video/animate'
  );
};

/**
 * Enhance a previously generated 480p draft preview to 1080p. Sends only the
 * draft handle + a fresh idempotency key; the backend reuses the preview's
 * inputs. Billed at the 1080p rate over the preview's duration. Uses the same
 * SSE + resume machinery as a normal generation.
 */
export const handleVideoDraftEnhancement = async (
  draftTaskId: string,
  token: string,
  onProgress?: (event: VideoProgressEvent) => void
): Promise<GenerateCharacterVideoResponse> => {
  if (typeof draftTaskId !== 'string' || !draftTaskId.trim()) {
    throw new Error('A draftTaskId is required to enhance a video');
  }
  const data: EnhanceVideoRequest = {
    draftTaskId: draftTaskId.trim(),
    clientRequestId: mintClientRequestId(),
  };
  return await runResumableVideoGeneration(data, token, onProgress);
};

/**
 * Mint a fresh idempotency key for one generation click. UUID v4 satisfies
 * the backend contract (8-128 chars `[A-Za-z0-9_-]`); the Math.random
 * fallback only runs where `crypto.randomUUID` is unavailable.
 */
export const mintClientRequestId = (): string => {
  try {
    const uuid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : '';
    if (/^[A-Za-z0-9_-]{8,128}$/.test(uuid)) return uuid;
  } catch {
    /* fall through to the Math.random fallback */
  }
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  let id = '';
  for (let i = 0; i < 32; i++) {
    id += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return id;
};

export type VideoSessionStatus = 'running' | 'complete' | 'failed';

export interface VideoGenerationSession {
  status: VideoSessionStatus;
  progress?: number;
  result?: any;
  error?: string;
  resolution?: VideoResolution | null;
  durationSeconds?: number | null;
  canEnhance?: boolean;
  sourceDraftGenerationId?: string | null;
}

/** Backoff between recovery rounds after a stream break (max ~5 tries). */
const RECOVERY_DELAYS_MS = [1000, 2000, 5000, 5000, 5000];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Internal signal: the stream broke before a terminal event. */
class StreamBreakError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StreamBreakError';
  }
}

const toNonEmptyStringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const buildCompletion = (
  parsed: any,
  videoUrl: string
): GenerateCharacterVideoResponse => ({
  videoUrl: normalizeVideoUrl(videoUrl),
  duration: parsed?.duration ?? parsed?.data?.duration,
  data: parsed?.data ?? parsed,
  ...(typeof parsed?.pixlesCharged === 'number'
    ? { pixlesCharged: parsed.pixlesCharged }
    : typeof parsed?.pixelsCharged === 'number'
      ? { pixlesCharged: parsed.pixelsCharged }
      : {}),
  ...(typeof parsed?.available === 'number' || parsed?.available === null
    ? { available: parsed.available }
    : {}),
  ...(parsed?.resolution === '480p' ||
  parsed?.resolution === '720p' ||
  parsed?.resolution === '1080p'
    ? { resolution: parsed.resolution as VideoResolution }
    : {}),
  ...(typeof parsed?.durationSeconds === 'number'
    ? { durationSeconds: parsed.durationSeconds }
    : {}),
  ...(toNonEmptyStringOrUndefined(parsed?.draftTaskId) !== undefined
    ? { draftTaskId: parsed.draftTaskId as string }
    : {}),
  ...(typeof parsed?.canEnhance === 'boolean'
    ? { canEnhance: parsed.canEnhance }
    : {}),
  ...(toNonEmptyStringOrUndefined(parsed?.enhancedFrom) !== undefined
    ? { enhancedFrom: parsed.enhancedFrom as string }
    : {}),
});

/**
 * Poll the resumable-generation session endpoint. Returns `null` on 404
 * (unknown/foreign key — treated as transient until retries are exhausted).
 */
const pollVideoSession = async (
  generationId: string,
  token: string
): Promise<VideoGenerationSession | null> => {
  const response = await fetch(
    `${API_BASE_URL}/generate/video/session/${encodeURIComponent(generationId)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const errorData = await response.text().catch(() => '');
    throwForCreditGate(response.status, errorData, {
      insufficient: 'Insufficient pixels for this video',
      noEntitlement: 'An active subscription is required for this video',
      failed: 'Failed to check video generation status',
    });
  }
  const parsed: any = await response.json();
  const status = parsed?.status;
  if (status !== 'running' && status !== 'complete' && status !== 'failed') {
    throw new Error('Video generation status response was not recognized');
  }
  const session: VideoGenerationSession = { status };
  if (typeof parsed?.progress === 'number') session.progress = parsed.progress;
  if (parsed?.result !== undefined) session.result = parsed.result;
  if (typeof parsed?.resolution === 'string' || parsed?.resolution === null) {
    session.resolution = parsed.resolution;
  }
  if (
    typeof parsed?.durationSeconds === 'number' ||
    parsed?.durationSeconds === null
  ) {
    session.durationSeconds = parsed.durationSeconds;
  }
  if (typeof parsed?.canEnhance === 'boolean') {
    session.canEnhance = parsed.canEnhance;
  }
  if (
    typeof parsed?.sourceDraftGenerationId === 'string' ||
    parsed?.sourceDraftGenerationId === null
  ) {
    session.sourceDraftGenerationId = parsed.sourceDraftGenerationId;
  }
  const sessionError = toNonEmptyStringOrUndefined(parsed?.error);
  if (sessionError !== undefined) session.error = sessionError;
  return session;
};

interface StreamConsumeState {
  /** Latest generationId seen on the stream; all resume/poll calls use it. */
  generationId?: string;
  completion?: GenerateCharacterVideoResponse;
}

/**
 * Consume one SSE stream to a terminal `complete` event. Throws the backend
 * `error` event as-is, and throws StreamBreakError on reader errors or EOF
 * without a completion. Handles `: ping` comments and `retry:` fields by
 * ignoring them; multi-line `data:` fields are joined per the SSE spec.
 */
const consumeVideoStream = async (
  body: ReadableStream<Uint8Array>,
  state: StreamConsumeState,
  onProgress?: (event: VideoProgressEvent) => void
): Promise<void> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let dataLines: string[] = [];

  const handleEvent = () => {
    if (dataLines.length === 0) return;
    const eventData = dataLines.join('\n');
    dataLines = [];
    if (eventData.trim() === '') return;
    let parsed: any;
    try {
      parsed = JSON.parse(eventData);
    } catch {
      console.warn('Failed to parse video event data:', eventData);
      return;
    }

    // Adopt the latest generation id seen — resume/poll always use it.
    const eventGenerationId = toNonEmptyStringOrUndefined(parsed?.generationId);
    if (eventGenerationId !== undefined) {
      state.generationId = eventGenerationId;
    }

    if (parsed?.status === 'error') {
      // The backend maps provider failures to user-safe copy; never append
      // raw `details` (may carry provider text) to the surfaced message.
      throw new Error(parsed?.message || 'Failed to generate video.');
    }

    if (parsed?.status === 'processing') {
      onProgress?.({
        ...parsed,
        status: parsed.status,
      } as VideoProgressEvent);
      return;
    }

    const videoUrl =
      getVideoUrlFromResponse(parsed) ??
      (typeof parsed?.videoUrl === 'string' ? parsed.videoUrl : undefined);
    if (parsed?.status === 'complete' || videoUrl) {
      if (!videoUrl) {
        throw new Error(
          'Video generation response did not include a video URL'
        );
      }
      onProgress?.({
        status: 'processing',
        message: 'Video generation complete',
        progress: 100,
      });
      state.completion = buildCompletion(parsed, videoUrl);
    }
  };

  try {
    while (true) {
      let read: ReadableStreamReadResult<Uint8Array>;
      try {
        read = await reader.read();
      } catch (error) {
        throw new StreamBreakError(
          error instanceof Error
            ? `Video generation stream broke: ${error.message}`
            : 'Video generation stream broke unexpectedly'
        );
      }
      if (read.done) break;
      buffer += decoder.decode(read.value, { stream: true });

      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';

      for (const block of blocks) {
        for (const rawLine of block.split('\n')) {
          const line = rawLine.trimEnd();
          if (line === '' || line.startsWith(':')) continue;
          if (line.startsWith('retry:')) continue;
          if (line.startsWith('event:')) continue;
          if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart());
            continue;
          }
          // A bare line is not valid SSE — ignore rather than fail the
          // whole 10-25 minute stream on one malformed frame.
        }
        handleEvent();
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* best effort — the stream is already done or broken */
    }
  }

  if (!state.completion) {
    throw new StreamBreakError(
      'Video generation stream ended without a video URL'
    );
  }
};

/**
 * Open one SSE stream: the fresh attempt or a `?resume=<generationId>`
 * reattach. Non-OK responses throw (402/403 via the shared credit gate);
 * a 404 on resume means the key is unknown.
 */
const openVideoStream = async (
  url: string,
  isResume: boolean,
  data: VideoRequestPayload,
  token: string
): Promise<ReadableStream<Uint8Array>> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Ask for the SSE progress stream (mirrors generateAudio): the backend
      // emits `processing` events with `progress` ~every 10s so 10-minute
      // seedance2_5 generations don't hit idle timeouts, then a final
      // `complete` event carrying the video URL.
      Accept: 'text/event-stream',
      Connection: 'keep-alive',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (isResume && response.status === 404) {
      throw new Error(
        'Video generation session was not found. Please start a new generation.'
      );
    }
    // Validation failures stay JSON (400) even on the SSE endpoint.
    const errorData = await response.text();
    // 402/403 throw the shared typed errors so callers can render
    // insufficient-credits / subscribe upsells instead of generic failures.
    throwForCreditGate(response.status, errorData, {
      insufficient: 'Insufficient pixels for this video',
      noEntitlement: 'An active subscription is required for this video',
      failed: 'Failed to generate video',
    });
  }

  // SSE-only endpoint.
  if (!response.body) {
    throw new Error('No stream returned from API');
  }
  return response.body;
};

/**
 * Fresh attempt plus stream-break recovery. On a break (reader error or EOF
 * without `complete`) with a known generationId, backs off (1s → 2s → 5s,
 * max ~5 tries), polls the session endpoint, and either renders a completed
 * result, reattaches via `?resume=`, or surfaces the failure. Recovery never
 * starts a fresh billable attempt: only resume replays/reattaches, and a
 * terminal `failed` settles the key — retrying needs a new click (which
 * mints a new `clientRequestId`).
 */
const runResumableVideoGeneration = async (
  data: VideoRequestPayload,
  token: string,
  onProgress?: (event: VideoProgressEvent) => void,
  endpoint: string = '/generate/video'
): Promise<GenerateCharacterVideoResponse> => {
  const freshUrl = `${API_BASE_URL}${endpoint}`;
  const state: StreamConsumeState = {};

  const announceReconnect = (progress?: number) => {
    if (typeof progress === 'number') {
      onProgress?.({
        status: 'processing',
        message: 'Connection interrupted. Reconnecting…',
        progress,
      });
    } else {
      onProgress?.({
        status: 'processing',
        message: 'Connection interrupted. Reconnecting…',
      });
    }
  };

  // Fresh attempt.
  try {
    await consumeVideoStream(
      await openVideoStream(freshUrl, false, data, token),
      state,
      onProgress
    );
    if (state.completion) return state.completion;
    throw new StreamBreakError(
      'Video generation stream ended without a video URL'
    );
  } catch (error) {
    // Terminal failures (backend `error` events, credit gates, validation)
    // surface immediately — only stream breaks are recoverable.
    if (!(error instanceof StreamBreakError)) throw error;
    // Without a generationId there is nothing to resume or poll.
    const generationId = state.generationId;
    if (!generationId) throw error;

    for (let attempt = 0; attempt < RECOVERY_DELAYS_MS.length; attempt++) {
      announceReconnect();
      await sleep(RECOVERY_DELAYS_MS[attempt]);

      let session: VideoGenerationSession | null;
      try {
        session = await pollVideoSession(generationId, token);
      } catch (pollError) {
        // Credit-gate errors are terminal; other poll failures are
        // transient until the budget is exhausted.
        if (
          pollError instanceof InsufficientPixlesError ||
          pollError instanceof NoActiveEntitlementError ||
          attempt === RECOVERY_DELAYS_MS.length - 1
        ) {
          throw pollError;
        }
        continue;
      }

      // 404 (unknown/foreign key) is transient until retries are exhausted.
      if (!session) {
        if (attempt === RECOVERY_DELAYS_MS.length - 1) {
          throw new Error(
            'Video generation session was not found. Please start a new generation.'
          );
        }
        continue;
      }

      if (typeof session.progress === 'number') {
        announceReconnect(session.progress);
      }

      if (session.status === 'failed') {
        // Settled server-side (refund already issued) — never auto-rebill.
        throw new Error(session.error || 'Video generation failed');
      }

      if (session.status === 'complete') {
        const result = session.result;
        const resultObject =
          result && typeof result === 'object' ? result : null;
        const videoUrl =
          (resultObject ? getVideoUrlFromResponse(resultObject) : undefined) ??
          (typeof result === 'string' && result !== '' ? result : undefined);
        if (!videoUrl) {
          throw new Error(
            'Video generation response did not include a video URL'
          );
        }
        onProgress?.({
          status: 'processing',
          message: 'Video generation complete',
          progress: 100,
        });
        return buildCompletion(
          result && typeof result === 'object' ? result : {},
          videoUrl
        );
      }

      // Still running — reattach and continue parsing the replayed stream.
      try {
        await consumeVideoStream(
          await openVideoStream(
            `${freshUrl}?resume=${encodeURIComponent(generationId)}`,
            true,
            data,
            token
          ),
          state,
          onProgress
        );
        if (state.completion) return state.completion;
      } catch (resumeError) {
        if (!(resumeError instanceof StreamBreakError)) throw resumeError;
        // Broke again — next round re-polls before reattaching.
      }
    }

    throw new Error(
      'Video generation stream broke and reconnection attempts were exhausted. Please try again — a retry starts a fresh generation.'
    );
  }
};

function normalizeVideoUrl(videoUrl: string): string {
  const trimmedUrl = videoUrl.trim();

  if (/^(https?:|blob:|data:)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith('/')) {
    return `${API_ORIGIN}${trimmedUrl}`;
  }

  return `${API_ORIGIN}/${trimmedUrl}`;
}
